import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  AtSign,
  Calendar,
  Check,
  Crown,
  Edit,
  Flag,
  MessageSquare,
  Save,
  Server,
  Shield,
  Sparkles,
  UserMinus,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { format } from 'date-fns';
import ReportModal from '../moderation/ReportModal';
import AppBadge from '../ui/AppBadge';
import { useAuthStore } from '../../store/authStore';
import { useFriendStore } from '../../store/friendStore';
import { useMessageStore } from '../../store/messageStore';
import { useServerStore } from '../../store/serverStore';
import { userAPI } from '../../lib/api';

interface ProfileViewProps {
  userId: string;
  isCurrentUser: boolean;
  onClose: () => void;
}

type SharedServer = {
  id: string;
  name: string;
  icon_url?: string | null;
};

type ProfileData = {
  id: string;
  username: string;
  display_name?: string | null;
  avatar_url?: string | null;
  banner_url?: string | null;
  bio?: string | null;
  status?: string;
  custom_status?: string | null;
  is_admin?: boolean;
  is_system?: boolean;
  bot?: boolean;
  created_at?: string;
  updated_at?: string;
  friend_count?: number;
  mutual_server_count?: number;
  relationship?: string;
  shared_servers?: SharedServer[];
};

const statusStyles: Record<string, string> = {
  online: 'bg-success',
  idle: 'bg-yellow-400',
  dnd: 'bg-error',
  offline: 'bg-text-muted',
};

function fallbackInitial(name?: string | null) {
  return (name || '?').charAt(0).toUpperCase();
}

const ProfileView: React.FC<ProfileViewProps> = ({ userId, isCurrentUser, onClose }) => {
  const { profile, updateProfile } = useAuthStore();
  const { setCurrentDmUser } = useMessageStore();
  const { setCurrentChannel } = useServerStore();
  const { friends, sendFriendRequest, removeFriend } = useFriendStore();

  const [isEditing, setIsEditing] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [bannerUrl, setBannerUrl] = useState('');
  const [customStatus, setCustomStatus] = useState('');
  const [bio, setBio] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [userData, setUserData] = useState<ProfileData | null>(null);

  const isFriend = !isCurrentUser && friends.some((friend) => friend.friend_id === userId);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const data = isCurrentUser && profile
          ? profile
          : await userAPI.getUserProfile(userId);

        setUserData(data);
        setDisplayName(data.display_name || '');
        setAvatarUrl(data.avatar_url || '');
        setBannerUrl(data.banner_url || '');
        setCustomStatus(data.custom_status || '');
        setBio(data.bio || '');
      } catch (err: any) {
        console.error('Failed to fetch profile:', err);
        setError('Impossible de charger le profil');
      } finally {
        setIsLoading(false);
      }
    };

    void fetchUserData();
  }, [userId, isCurrentUser, profile]);

  const displayTitle = userData?.display_name || userData?.username || 'Utilisateur';
  const status = userData?.status || 'offline';
  const joinedLabel = userData?.created_at ? format(new Date(userData.created_at), 'MMMM yyyy') : 'Inconnu';
  const updatedLabel = userData?.updated_at ? format(new Date(userData.updated_at), 'dd MMM yyyy') : null;
  const relationshipLabel = useMemo(() => {
    if (userData?.bot) return 'Application';
    if (isCurrentUser) return 'Votre profil';
    if (isFriend || userData?.relationship === 'accepted') return 'Ami';
    if (userData?.relationship === 'pending') return 'Demande en attente';
    return 'Pas encore ami';
  }, [isCurrentUser, isFriend, userData?.bot, userData?.relationship]);

  const handleSaveProfile = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await updateProfile({
        display_name: displayName,
        avatar_url: avatarUrl,
        banner_url: bannerUrl,
        custom_status: customStatus,
        bio,
      });
      setUserData((previous) => previous ? {
        ...previous,
        display_name: displayName,
        avatar_url: avatarUrl,
        banner_url: bannerUrl,
        custom_status: customStatus,
        bio,
        updated_at: new Date().toISOString(),
      } : previous);
      setSuccess('Profil mis à jour');
      setIsEditing(false);
    } catch (err: any) {
      setError(err.message || 'Échec de la mise à jour du profil');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = () => {
    setCurrentChannel(null);
    setCurrentDmUser(userId);
    onClose();
  };

  const handleAddFriend = async () => {
    if (!userData?.username) return;
    try {
      setIsLoading(true);
      await sendFriendRequest(userData.username);
      setSuccess('Demande d’ami envoyée');
    } catch (err: any) {
      setError(err.message || 'Échec de l’envoi de la demande');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveFriend = async () => {
    try {
      setIsLoading(true);
      await removeFriend(userId);
      setSuccess('Ami supprimé');
    } catch (err: any) {
      setError(err.message || 'Échec de la suppression');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && !userData) {
    return (
      <div className="w-[560px] max-w-[calc(100vw-32px)] h-[420px] rounded-xl bg-bg-secondary border border-border-color flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-accent-color" />
      </div>
    );
  }

  if (error && !userData) {
    return (
      <div className="w-[520px] max-w-[calc(100vw-32px)] rounded-xl bg-bg-secondary border border-border-color p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-text-primary">Profil indisponible</h3>
          <button className="text-text-secondary hover:text-text-primary" onClick={onClose}>
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="text-error flex flex-col items-center gap-2 py-8">
          <AlertCircle className="h-8 w-8" />
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-[560px] max-w-[calc(100vw-32px)] max-h-[86vh] overflow-hidden rounded-xl bg-bg-secondary border border-border-color shadow-2xl animate-fade-in">
      <div className="relative">
        {userData?.banner_url ? (
          <img src={userData.banner_url} alt="" className="h-32 w-full object-cover" />
        ) : (
          <div className="h-32 bg-gradient-to-br from-accent-color/70 via-blue-600/60 to-fuchsia-600/60" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-bg-secondary/80 to-transparent" />
        <button
          className="absolute top-3 right-3 bg-black/30 hover:bg-black/45 text-white p-2 rounded-lg transition-colors"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </button>
        <div className="absolute -bottom-10 left-5">
          <div className="relative">
            {userData?.avatar_url ? (
              <img
                src={userData.avatar_url}
                alt={displayTitle}
                className="w-20 h-20 rounded-2xl object-cover ring-4 ring-bg-secondary"
              />
            ) : (
              <div className="w-20 h-20 rounded-2xl bg-accent-color text-white flex items-center justify-center text-3xl font-bold ring-4 ring-bg-secondary">
                {fallbackInitial(displayTitle)}
              </div>
            )}
            <span className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-4 border-bg-secondary ${statusStyles[status] || statusStyles.offline}`} />
          </div>
        </div>
      </div>

      <div className="overflow-y-auto max-h-[calc(86vh-128px)] p-5 pt-14 scrollbar-thin">
        {error && (
          <div className="bg-error/10 border border-error/30 text-error p-2 rounded-lg mb-3 text-xs flex items-center">
            <AlertCircle className="h-3 w-3 mr-1 flex-shrink-0" />
            {error}
          </div>
        )}
        {success && (
          <div className="bg-success/10 border border-success/30 text-success p-2 rounded-lg mb-3 text-xs flex items-center">
            <Check className="h-3 w-3 mr-1 flex-shrink-0" />
            {success}
          </div>
        )}

        {isEditing ? (
          <form onSubmit={handleSaveProfile} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs font-medium text-text-secondary">
                Nom affiché
                <input
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                  className="input w-full mt-1 text-sm"
                  placeholder={userData?.username}
                />
              </label>
              <label className="text-xs font-medium text-text-secondary">
                Statut
                <input
                  value={customStatus}
                  onChange={(event) => setCustomStatus(event.target.value)}
                  className="input w-full mt-1 text-sm"
                  maxLength={50}
                />
              </label>
            </div>
            <label className="text-xs font-medium text-text-secondary block">
              URL avatar
              <input value={avatarUrl} onChange={(event) => setAvatarUrl(event.target.value)} className="input w-full mt-1 text-sm" />
            </label>
            <label className="text-xs font-medium text-text-secondary block">
              URL bannière
              <input value={bannerUrl} onChange={(event) => setBannerUrl(event.target.value)} className="input w-full mt-1 text-sm" />
            </label>
            <label className="text-xs font-medium text-text-secondary block">
              Bio
              <textarea
                value={bio}
                onChange={(event) => setBio(event.target.value)}
                rows={4}
                maxLength={300}
                className="input w-full mt-1 resize-none text-sm"
              />
              <span className="block text-right text-text-muted mt-1">{bio.length}/300</span>
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setIsEditing(false)} className="btn btn-secondary text-xs">
                Annuler
              </button>
              <button type="submit" disabled={isLoading} className="btn btn-primary text-xs inline-flex items-center gap-1.5">
                <Save className="h-3.5 w-3.5" />
                Enregistrer
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold text-text-primary truncate">{displayTitle}</h2>
                  {userData?.is_admin && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-accent-color/15 text-accent-color border border-accent-color/30">
                      <Shield className="h-3 w-3" />
                      ADMIN
                    </span>
                  )}
                  {userData?.bot && <AppBadge />}
                </div>
                <div className="flex items-center gap-1 text-sm text-text-secondary mt-1">
                  <AtSign className="h-3.5 w-3.5" />
                  <span>{userData?.username}</span>
                </div>
                {userData?.custom_status && (
                  <p className="text-sm text-text-primary mt-2">{userData.custom_status}</p>
                )}
              </div>
              {isCurrentUser && (
                <button
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-highlight hover:bg-highlight-hover text-text-primary text-xs transition-colors"
                  onClick={() => setIsEditing(true)}
                >
                  <Edit className="h-3.5 w-3.5" />
                  Modifier
                </button>
              )}
            </div>

            {!isCurrentUser && !userData?.is_system && (
              <div className="grid grid-cols-2 gap-2">
                <button className="btn btn-secondary py-2 text-xs flex items-center justify-center" onClick={handleSendMessage}>
                  <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                  Message
                </button>
                {isFriend ? (
                  <button className="btn btn-secondary py-2 text-xs flex items-center justify-center text-error" onClick={handleRemoveFriend}>
                    <UserMinus className="h-3.5 w-3.5 mr-1.5" />
                    Retirer
                  </button>
                ) : (
                  <button className="btn btn-primary py-2 text-xs flex items-center justify-center" onClick={handleAddFriend}>
                    <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                    Ajouter
                  </button>
                )}
              </div>
            )}

            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-lg bg-bg-tertiary p-3">
                <Users className="h-4 w-4 text-text-muted mb-2" />
                <p className="text-lg font-bold text-text-primary">{userData?.friend_count ?? 0}</p>
                <p className="text-[10px] uppercase text-text-muted">Amis</p>
              </div>
              <div className="rounded-lg bg-bg-tertiary p-3">
                <Server className="h-4 w-4 text-text-muted mb-2" />
                <p className="text-lg font-bold text-text-primary">{userData?.mutual_server_count ?? 0}</p>
                <p className="text-[10px] uppercase text-text-muted">Serveurs</p>
              </div>
              <div className="rounded-lg bg-bg-tertiary p-3">
                <Sparkles className="h-4 w-4 text-text-muted mb-2" />
                <p className="text-sm font-bold text-text-primary truncate">{relationshipLabel}</p>
                <p className="text-[10px] uppercase text-text-muted">Relation</p>
              </div>
            </div>

            <section className="rounded-lg bg-bg-tertiary p-4">
              <h3 className="text-xs font-semibold text-text-secondary uppercase mb-2">À propos</h3>
              <p className="text-text-primary text-sm whitespace-pre-wrap leading-relaxed">
                {userData?.bio || 'Aucune description.'}
              </p>
            </section>

            <section className="rounded-lg bg-bg-tertiary p-4 space-y-3">
              <h3 className="text-xs font-semibold text-text-secondary uppercase">Détails</h3>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-2 text-text-secondary">
                  <Calendar className="h-3.5 w-3.5 text-text-muted" />
                  <span>Membre depuis {joinedLabel}</span>
                </div>
                {updatedLabel && (
                  <div className="flex items-center gap-2 text-text-secondary">
                    <Crown className="h-3.5 w-3.5 text-text-muted" />
                    <span>Profil modifié {updatedLabel}</span>
                  </div>
                )}
              </div>
            </section>

            {!!userData?.shared_servers?.length && (
              <section className="rounded-lg bg-bg-tertiary p-4">
                <h3 className="text-xs font-semibold text-text-secondary uppercase mb-3">Serveurs en commun</h3>
                <div className="grid grid-cols-2 gap-2">
                  {userData.shared_servers.map((server) => (
                    <div key={server.id} className="flex items-center gap-2 min-w-0 rounded-md bg-bg-secondary/70 px-2 py-2">
                      {server.icon_url ? (
                        <img src={server.icon_url} alt="" className="w-6 h-6 rounded-md object-cover" />
                      ) : (
                        <div className="w-6 h-6 rounded-md bg-highlight flex items-center justify-center text-[10px] font-bold">
                          {fallbackInitial(server.name)}
                        </div>
                      )}
                      <span className="text-xs text-text-primary truncate">{server.name}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {!isCurrentUser && !userData?.is_system && (
              <button
                className="w-full btn btn-secondary py-2 text-xs flex items-center justify-center text-text-muted hover:text-error"
                onClick={() => setShowReport(true)}
              >
                <Flag className="h-3.5 w-3.5 mr-1.5" />
                Signaler cet utilisateur
              </button>
            )}
          </div>
        )}
      </div>

      {showReport && userData && (
        <ReportModal
          target={{
            type: 'user',
            id: userId,
            reported_user_id: userId,
            label: `@${userData.username}`,
          }}
          onClose={() => setShowReport(false)}
        />
      )}
    </div>
  );
};

export default ProfileView;
